import { Fragment, useCallback, useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Switch } from '@/components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AppAccessModeSwitch,
  AppExclusionList,
  AppExclusionsProvider,
  categoryRowPresentation,
  lockedByLabel,
  useAppExclusionsContext,
} from '@/features/app-exclusions';
import {
  type DomainWithExclusionState,
  electronApis,
} from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { inElectron } from '@/utils';
import { listenWithAbortIPC } from '@/utils/abort';
import { handleExternalUrlClick } from '@/utils/platform';

import { domainSchema } from '../domain-input';
import { SettingsSection } from '../settings-section';

export const PrivacyControlsSettings = () => (
  <AppExclusionsProvider>
    <PrivacyControlsContent />
  </AppExclusionsProvider>
);

const PrivacyControlsContent = () => {
  const { enabledCategoryIds } = useAppExclusionsContext();

  const [domainsLoading, setDomainsLoading] = useState(true);
  const [excludedDomains, setExcludedDomains] = useState<
    DomainWithExclusionState[]
  >([]);
  const [domainsError, setDomainsError] = useState<string | null>(null);

  // User-included domains: sites kept captured even when their category is
  // excluded.
  const [includedDomainsLoading, setIncludedDomainsLoading] = useState(true);
  const [includedDomains, setIncludedDomains] = useState<
    DomainWithExclusionState[]
  >([]);
  const [includedDomainsError, setIncludedDomainsError] = useState<
    string | null
  >(null);

  const [activeTab, setActiveTab] = useState('apps');

  // Fetch functions
  const fetchDomainExclusions = useCallback(async () => {
    try {
      const domains = await electronApis.appExclusion.getDomainExclusions();
      setExcludedDomains(domains);
      setDomainsError(null);
    } catch (err) {
      setDomainsError(`Failed to load domain exclusions: ${err}`);
      logError(err, 'Failed to load domain exclusions');
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  const fetchIncludedDomains = useCallback(async () => {
    try {
      const domains = await electronApis.appExclusion.getIncludedDomains();
      setIncludedDomains(domains);
      setIncludedDomainsError(null);
    } catch (err) {
      setIncludedDomainsError(`Failed to load included websites: ${err}`);
      logError(err, 'Failed to load included domains');
    } finally {
      setIncludedDomainsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!inElectron()) return;
    fetchDomainExclusions();
    fetchIncludedDomains();
  }, [fetchDomainExclusions, fetchIncludedDomains]);

  useEffect(() => {
    if (!inElectron()) return;
    const unsubscribe = listenWithAbortIPC<{
      excludedDomains?: DomainWithExclusionState[];
      includedDomains?: DomainWithExclusionState[];
    }>('exclusions-updated', (data) => {
      if (data?.excludedDomains) {
        setExcludedDomains(data.excludedDomains);
      }
      // Keep the included list live with status-bar "Include" actions.
      if (data?.includedDomains) {
        setIncludedDomains(data.includedDomains);
      }
    });
    return () => unsubscribe?.();
  }, []);

  const domainsCount = excludedDomains.length;
  const includedCount = includedDomains.length;
  const categoriesCount = enabledCategoryIds.length;

  const formatTabLabel = (label: string, count: number) => {
    if (count === 0) return label;
    return `${label} (${count})`;
  };

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Privacy Controls</SettingsSection.Title>
        <SettingsSection.Description>
          Control what Littlebird can see. Excluded content won't appear in your
          context.{' '}
          <button
            onClick={() =>
              handleExternalUrlClick(
                'https://support.littlebird.ai/docs/exclusions/'
              )
            }
            className="inline-flex items-center gap-1 underline underline-offset-4 hover:text-foreground transition-colors"
          >
            Learn more
            <Icons.externalLink2 className="size-3 shrink-0" />
          </button>
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <AppAccessModeSwitch />
      </SettingsSection.Group>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
        size="lg"
      >
        <TabsList>
          <TabsTrigger value="apps">Apps</TabsTrigger>
          <TabsTrigger value="websites">
            {formatTabLabel('Websites', categoriesCount + domainsCount)}
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="apps"
          forceMount
          className="data-[state=inactive]:hidden"
        >
          <AppExclusionList showSwitchCoachMark={activeTab === 'apps'} />
        </TabsContent>
        <TabsContent
          value="websites"
          forceMount
          className="data-[state=inactive]:hidden"
        >
          <Accordion
            type="multiple"
            defaultValue={['categories', 'websites', 'included']}
            className="w-full"
          >
            <AccordionItem
              value="categories"
              className="border-b border-border-strong"
            >
              <AccordionTrigger className="hover:no-underline pt-1">
                <XStack className="items-center gap-2">
                  <Icons.chevronDown className="size-4 shrink-0 text-foreground-muted transition-transform duration-200" />
                  <span>Website Categories</span>
                </XStack>
              </AccordionTrigger>
              <AccordionContent>
                <CategoryExclusionSettings />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem
              value="websites"
              className="border-b border-border-strong"
            >
              <AccordionTrigger className="hover:no-underline">
                <XStack className="items-center gap-2">
                  <Icons.chevronDown className="size-4 shrink-0 text-foreground-muted transition-transform duration-200" />
                  <span>Exclude Specific Websites</span>
                  {domainsCount > 0 && (
                    <span className="text-foreground-muted">
                      ({domainsCount})
                    </span>
                  )}
                </XStack>
              </AccordionTrigger>
              <AccordionContent>
                <DomainListEditor
                  loading={domainsLoading}
                  domains={excludedDomains}
                  setDomains={setExcludedDomains}
                  error={domainsError}
                  setError={setDomainsError}
                  persist={(d) =>
                    electronApis.appExclusion.updateDomainExclusions(d)
                  }
                  refetch={() =>
                    electronApis.appExclusion.getDomainExclusions()
                  }
                  inputPlaceholder="Add domain (example.com)"
                  loadingText="Loading excluded domains..."
                  emptyText="No domains excluded yet."
                  duplicateMessage="Domain is already excluded."
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="included" className="border-b-0">
              <AccordionTrigger className="hover:no-underline">
                <XStack className="items-center gap-2">
                  <Icons.chevronDown className="size-4 shrink-0 text-foreground-muted transition-transform duration-200" />
                  <span>Always Include Specific Websites</span>
                  {includedCount > 0 && (
                    <span className="text-foreground-muted">
                      ({includedCount})
                    </span>
                  )}
                </XStack>
              </AccordionTrigger>
              <AccordionContent>
                <YStack className="gap-3 w-full">
                  <p className="text-foreground-muted text-xs">
                    Always capture these websites, even when they match an
                    excluded category above.
                  </p>
                  <DomainListEditor
                    loading={includedDomainsLoading}
                    domains={includedDomains}
                    setDomains={setIncludedDomains}
                    error={includedDomainsError}
                    setError={setIncludedDomainsError}
                    persist={(d) =>
                      electronApis.appExclusion.updateIncludedDomains(d)
                    }
                    refetch={() =>
                      electronApis.appExclusion.getIncludedDomains()
                    }
                    inputPlaceholder="Always include domain (example.com)"
                    loadingText="Loading included websites..."
                    emptyText="No websites are always included yet."
                    duplicateMessage="Website is already included."
                  />
                </YStack>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>
    </SettingsSection.Root>
  );
};

const CategoryExclusionSettings = () => {
  const {
    categories,
    enabledCategoryIds,
    categoriesLoading,
    categoriesError,
    toggleCategory,
  } = useAppExclusionsContext();

  if (categoriesLoading) {
    return (
      <div className="text-sm text-foreground-muted">Loading categories...</div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-sm text-foreground-muted">
        No categories available
      </div>
    );
  }

  return (
    <YStack className="gap-3">
      <p className="text-foreground-muted text-xs">
        Turn off a category to exclude its websites. Other websites stay
        included unless you exclude them below.
      </p>
      {categoriesError && (
        <div className="text-xs text-error-foreground bg-error/10 p-2 rounded">
          {categoriesError}
        </div>
      )}
      <SettingsSection.Group>
        {categories.map((category, index) => {
          const { checked, switchLabel, status } = categoryRowPresentation(
            category,
            enabledCategoryIds.includes(category.id)
          );
          return (
            <Fragment key={category.id}>
              <SettingsSection.Item
                direction="horizontal"
                onClick={() => toggleCategory(category.id)}
              >
                <SettingsSection.ItemHeader>
                  <SettingsSection.ItemLabel>
                    {category.name}
                  </SettingsSection.ItemLabel>
                  <SettingsSection.ItemDescription>
                    {category.description}
                  </SettingsSection.ItemDescription>
                </SettingsSection.ItemHeader>
                <XStack className="shrink-0 items-center gap-3">
                  <span className="text-xs text-foreground-muted">
                    {status}
                  </span>
                  <Switch checked={checked} aria-label={switchLabel} />
                </XStack>
              </SettingsSection.Item>
              {index !== categories.length - 1 && <SettingsSection.Seperator />}
            </Fragment>
          );
        })}
      </SettingsSection.Group>
    </YStack>
  );
};

interface DomainListEditorProps {
  loading: boolean;
  domains: DomainWithExclusionState[];
  setDomains: React.Dispatch<React.SetStateAction<DomainWithExclusionState[]>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  /** Persist the full list of user-editable domains, returning success. */
  persist: (domains: string[]) => Promise<{ success: boolean; error?: string }>;
  /** Re-read the canonical list after a write (picks up server-side merges). */
  refetch: () => Promise<DomainWithExclusionState[]>;
  inputPlaceholder: string;
  loadingText: string;
  emptyText: string;
  duplicateMessage: string;
}

/**
 * Shared editor for a list of domains (add via validated input, remove via row
 * button) used for both the excluded and included website lists. Rows flagged
 * `canUserModify: false` (default exclusions) render a badge and are read-only.
 */
const DomainListEditor = ({
  loading,
  domains,
  setDomains,
  error,
  setError,
  persist,
  refetch,
  inputPlaceholder,
  loadingText,
  emptyText,
  duplicateMessage,
}: DomainListEditorProps) => {
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRemove = async (domainItem: DomainWithExclusionState) => {
    if (!domainItem.canUserModify) {
      setError(
        domainItem.lockedBy === 'team'
          ? 'Cannot remove sites excluded by your team'
          : 'Cannot remove default excluded domains'
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const domainStrings = domains
        .filter((d) => d.domain !== domainItem.domain)
        .map((d) => d.domain);
      const result = await persist(domainStrings);
      if (result.success) {
        setDomains(domains.filter((d) => d.domain !== domainItem.domain));
      } else {
        setError(`Failed to remove domain: ${result.error}`);
      }
    } catch (err) {
      setError(`Failed to remove domain: ${err}`);
      logError(err, 'Failed to remove domain');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = async () => {
    setError(null);
    setSubmitting(true);

    try {
      const validationResult = domainSchema.safeParse(inputValue);

      if (!validationResult.success) {
        const errorMessage =
          validationResult.error.issues[0]?.message ||
          'Please enter a valid domain name.';
        setError(errorMessage);
        setSubmitting(false);
        return;
      }

      const domain = validationResult.data;

      if (domains.some((d) => d.domain === domain)) {
        setError(duplicateMessage);
        setSubmitting(false);
        return;
      }

      const domainStrings = domains.map((d) => d.domain);
      const newDomains = [...domainStrings, domain];
      const result = await persist(newDomains);
      if (result.success) {
        const updatedDomains = await refetch();
        setDomains(updatedDomains);
        setInputValue('');
      } else {
        // result.error is already a user-readable message (e.g. protected domain).
        setError(result.error || 'Failed to add domain.');
      }
    } catch (err) {
      setError(`Failed to add domain: ${err}`);
      logError(err, 'Failed to add domain');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <YStack className="gap-4 w-full">
      {loading ? (
        <div className="text-xs text-foreground-muted p-2">{loadingText}</div>
      ) : (
        <>
          <XStack className="gap-2 items-center w-full">
            <Input
              type="text"
              placeholder={inputPlaceholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-1"
              disabled={submitting}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
            <Button
              variant="outline"
              className="text-sm whitespace-nowrap"
              onClick={handleAdd}
              isLoading={submitting}
              disabled={submitting || !inputValue.trim()}
            >
              Add
            </Button>
          </XStack>
          {error && <div className="text-danger-9 text-xs">{error}</div>}
          <ScrollArea className="max-h-[440px] bg-surface border border-border rounded-lg py-2 px-2">
            <YStack className="gap-1">
              {domains.length === 0 ? (
                <div className="text-xs text-foreground-muted p-2">
                  {emptyText}
                </div>
              ) : (
                domains.map((domainItem) => (
                  <XStack
                    key={domainItem.domain}
                    className={cn(
                      'items-center justify-between rounded px-2 py-1 gap-2',
                      domainItem.canUserModify
                        ? 'bg-surface-muted'
                        : 'bg-surface-muted opacity-70 cursor-not-allowed'
                    )}
                  >
                    <XStack className="items-center gap-2 flex-1 min-w-0">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${domainItem.domain}&sz=32`}
                        alt={`${domainItem.domain} favicon`}
                        className="size-4 shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <span className="text-sm truncate flex items-center">
                        {domainItem.domain}
                        {!domainItem.canUserModify && (
                          <span className="ml-1 text-foreground-muted inline-block">
                            {lockedByLabel(domainItem.lockedBy)}
                          </span>
                        )}
                      </span>
                    </XStack>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => handleRemove(domainItem)}
                      className="text-danger-8 px-1 py-0 shrink-0"
                      disabled={submitting || !domainItem.canUserModify}
                      aria-label={`Remove ${domainItem.domain}`}
                    >
                      <Icons.cancel className="size-3" />
                    </Button>
                  </XStack>
                ))
              )}
            </YStack>
          </ScrollArea>
        </>
      )}
    </YStack>
  );
};
